import React, { useEffect, useRef } from "react";
import { useTranslation } from "./i18n/useTranslation";
import { useFeatureFlags } from "./features";

import {
  View,
  Text,
  StyleSheet,
  Animated,
  useWindowDimensions,
} from "react-native";

const PARTICLE_COUNT = 30;

export default function RewardOverlay({ visible, onHide }) {
  const opacity = useRef(new Animated.Value(0)).current;
  const { t } = useTranslation();
  const flags = useFeatureFlags();
  const { width, height } = useWindowDimensions();
  const sbs3dProjectorMode = flags?.sbs3d === true;

  const particles = useRef(
    Array.from({ length: PARTICLE_COUNT }).map(() => ({
      x: new Animated.Value(0),
      y: new Animated.Value(0),
      opacity: new Animated.Value(1),
      scale: new Animated.Value(1),
    }))
  ).current;

  useEffect(() => {
    if (!visible) return;

    opacity.setValue(1);

    // Re-seed from the CURRENT fullscreen dimensions each time. This matters
    // in projector mode because the reward provider is mounted before the user
    // enters browser fullscreen.
    particles.forEach((p) => {
      p.x.setValue(Math.random() * width);
      p.y.setValue(height + Math.random() * 100);
      p.opacity.setValue(1);
      p.scale.setValue(0.5 + Math.random());
    });

    const animations = particles.map(p =>
      Animated.parallel([
        Animated.timing(p.y, {
          toValue: Math.random() * height * 0.6,
          duration: 1200 + Math.random() * 600,
          useNativeDriver: true,
        }),
        Animated.timing(p.opacity, {
          toValue: 0,
          duration: 1800,
          useNativeDriver: true,
        }),
        Animated.timing(p.scale, {
          toValue: 1.2,
          duration: 1200,
          useNativeDriver: true,
        }),
      ])
    );

    Animated.sequence([
      Animated.parallel(animations),
      Animated.timing(opacity, {
        toValue: 0,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onHide && onHide();
    });
  // A reward is a one-shot event. Fullscreen enter/exit changes width/height
  // and must NOT replay an already-earned reward. RewardProvider remounts this
  // component with a new key for each real showReward() call, so the current
  // dimensions are still captured correctly for each new reward.
  }, [visible]);

  if (!visible) return null;

  const renderRewardContent = (keyPrefix = "reward") => (
    <>
      <View style={[styles.textContainer, { top: height / 2 - 20 }]}>
        <Text style={styles.text}>🏆 {t("Points Earned!")}</Text>
      </View>

      {particles.map((p, i) => (
        <Animated.View
          key={`${keyPrefix}-particle-${i}`}
          style={[
            styles.particle,
            {
              opacity: p.opacity,
              transform: [
                { translateX: p.x },
                { translateY: p.y },
                { scale: p.scale },
              ],
            },
          ]}
        />
      ))}
    </>
  );

  // RewardProvider is a sibling of the AITutor SBS-composited root, so the
  // normal projector filter cannot duplicate this overlay. In SBS mode we
  // explicitly render the SAME logical reward scene into both physical halves.
  if (sbs3dProjectorMode) {
    return (
      <Animated.View
        pointerEvents="none"
        style={[styles.sbsRoot, { opacity }]}
      >
        {[0, 1].map((eyeIndex) => (
          <View
            key={`reward-eye-${eyeIndex}`}
            style={[
              styles.sbsEye,
              { left: eyeIndex === 0 ? 0 : "50%" },
            ]}
          >
            <View
              style={[
                styles.sbsLogicalEye,
                {
                  width,
                  height,
                  transform: [{ scaleX: 0.5 }],
                },
              ]}
            >
              {renderRewardContent(`eye-${eyeIndex}`)}
            </View>
          </View>
        ))}
      </Animated.View>
    );
  }

  return (
    <Animated.View
      pointerEvents="none"
      style={[styles.root, { width, height, opacity }]}
    >
      {renderRewardContent()}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  root: {
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 999,
  },

  sbsRoot: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 2147483647,
    overflow: "hidden",
  },

  sbsEye: {
    position: "absolute",
    top: 0,
    width: "50%",
    height: "100%",
    overflow: "hidden",
  },

  sbsLogicalEye: {
    position: "absolute",
    top: 0,
    left: 0,
    transformOrigin: "0 0",
  },

  textContainer: {
    position: "absolute",
    width: "100%",
    alignItems: "center",
  },

  text: {
    fontSize: 22,
    fontWeight: "700",
    color: "rgb(0, 119, 182)",
  },

  particle: {
    position: "absolute",
    width: 8,
    height: 14,
    borderRadius: 4,
    backgroundColor: "#f59e0b",
    shadowColor: "#f59e0b",
    shadowOpacity: 0.8,
    shadowRadius: 6,
    elevation: 5,
  },
});
